/**
 * Koï favourites — an app-free, account-free wishlist kept in the visitor's browser.
 *
 * Storage: localStorage key `koiFavourites`, a JSON array of product handles (oldest first, max 50).
 * Elements:
 *   <koi-fav-toggle data-handle="…">  heart button; `data-mode="remove"` turns it into a remove-only button
 *   <koi-fav-count>                    header bubble (reuses the cart-bubble classes), hidden at 0
 *   <koi-fav-list data-section="koi-favourite-card" data-root="…">
 *                                      favourites page: one Section Rendering API fetch per handle
 * Event: `koi:favourites` on document, detail { handles, handle, added, external }.
 * Snippets: koi-favourite-toggle.liquid, koi-favourite-styles.liquid · sections: koi-favourites, koi-favourite-card.
 */

const KEY = 'koiFavourites';
const MAX = 50;
const EVENT = 'koi:favourites';
const HANDLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;
const CHUNK = 6;

/** @type {string | null} in-memory fallback when localStorage throws (private mode, quota) */
let memory = null;

/** @returns {string[]} */
function read() {
  let raw = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    raw = memory;
  }
  let list;
  try {
    list = JSON.parse(raw || '[]');
  } catch {
    list = [];
  }
  if (!Array.isArray(list)) list = [];
  const seen = new Set();
  const out = [];
  for (const handle of list) {
    if (typeof handle === 'string' && HANDLE_RE.test(handle) && !seen.has(handle)) {
      seen.add(handle);
      out.push(handle);
    }
  }
  return out.slice(-MAX);
}

/**
 * @param {string[]} list
 * @param {{ handle?: string, added?: boolean }} [detail]
 */
function write(list, detail = {}) {
  const json = JSON.stringify(list);
  try {
    localStorage.setItem(KEY, json);
  } catch {
    memory = json;
  }
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles: list, external: false, ...detail } }));
}

export const favourites = {
  list: () => read(),
  /** @param {string} handle */
  has: (handle) => read().includes(handle),
  /** @param {string} handle */
  add(handle) {
    if (!HANDLE_RE.test(handle)) return;
    const list = read().filter((h) => h !== handle);
    list.push(handle);
    while (list.length > MAX) list.shift();
    write(list, { handle, added: true });
  },
  /** @param {string} handle */
  remove(handle) {
    const list = read();
    if (!list.includes(handle)) return;
    write(
      list.filter((h) => h !== handle),
      { handle, added: false }
    );
  },
  /** @param {string} handle */
  toggle(handle) {
    if (read().includes(handle)) this.remove(handle);
    else this.add(handle);
  },
};

// Debug hook (window.koiFavourites.list()).
window.koiFavourites = favourites;

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key === KEY || event.key === null) {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles: read(), external: true } }));
  }
});

class KoiFavToggle extends HTMLElement {
  static observedAttributes = ['data-handle'];

  /** @type {MutationObserver | null} */
  #observer = null;

  #onClick = (/** @type {MouseEvent} */ event) => {
    const handle = this.dataset.handle;
    if (!handle) return;
    event.preventDefault();
    if (this.dataset.mode === 'remove') favourites.remove(handle);
    else favourites.toggle(handle);
  };

  #onChange = () => this.sync();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(EVENT, this.#onChange);
    const button = this.querySelector('button');
    // Ritual's section morphing copies attributes from server HTML onto the existing element
    // (aria-pressed → "false", a new data-handle); re-sync whenever that happens.
    if (button && !this.#observer) {
      this.#observer = new MutationObserver(() => this.sync());
      this.#observer.observe(button, { attributes: true, attributeFilter: ['aria-pressed'] });
      this.#observer.observe(this, { attributes: true, attributeFilter: ['data-active'] });
    }
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(EVENT, this.#onChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.sync();
  }

  /** Reflect the stored state; only writes attributes whose value differs (the observer above would loop otherwise). */
  sync() {
    const active = favourites.has(this.dataset.handle || '');
    if (this.hasAttribute('data-active') !== active) this.toggleAttribute('data-active', active);
    const button = this.querySelector('button');
    if (!button || this.dataset.mode === 'remove') return;
    const pressed = active ? 'true' : 'false';
    if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    const label = active ? this.dataset.labelOn : this.dataset.labelOff;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  }
}

class KoiFavCount extends HTMLElement {
  /** @type {number | null} */
  #previous = null;

  #onChange = (/** @type {Event} */ event) => this.update(/** @type {CustomEvent} */ (event).detail);

  connectedCallback() {
    document.addEventListener(EVENT, this.#onChange);
    window.addEventListener('pageshow', this.#onChange);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
    window.removeEventListener('pageshow', this.#onChange);
  }

  /** @param {{ added?: boolean, external?: boolean } | undefined} [detail] */
  update(detail) {
    const count = favourites.list().length;
    const text = this.querySelector('.cart-bubble__text-count');
    if (text) {
      text.textContent = count > 0 && count < 100 ? String(count) : '';
      text.classList.toggle('hidden', count === 0);
    }
    this.classList.toggle('visually-hidden', count === 0);
    this.closest('.header-actions__cart-icon')?.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    const link = this.closest('a');
    const template = link?.dataset.labelTemplate;
    if (link && template) link.setAttribute('aria-label', template.replace('@@', String(count)));
    if (detail?.added && !detail.external && this.#previous !== null && count > this.#previous) {
      this.classList.add('cart-bubble--animating');
      this.addEventListener('animationend', () => this.classList.remove('cart-bubble--animating'), { once: true });
    }
    this.#previous = count;
  }
}

class KoiFavList extends HTMLElement {
  #busy = false;
  #dirty = false;
  /** @type {Map<string, Element>} */
  #cards = new Map();

  #onChange = () => {
    if (this.#busy) {
      this.#dirty = true;
      return;
    }
    this.render();
  };

  connectedCallback() {
    document.addEventListener(EVENT, this.#onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
  }

  async render() {
    const grid = this.querySelector('[data-koi-grid]');
    if (!grid) return;
    this.#busy = true;
    const empty = this.querySelector('[data-koi-empty]');
    const status = this.querySelector('[data-koi-status]');
    const handles = favourites.list().slice().reverse(); // newest first

    for (const [handle, card] of this.#cards) {
      if (!handles.includes(handle)) {
        card.remove();
        this.#cards.delete(handle);
      }
    }

    const missing = handles.filter((handle) => !this.#cards.has(handle));
    if (missing.length) {
      grid.setAttribute('aria-busy', 'true');
      if (status) status.textContent = this.dataset.loadingText || '';
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const section = this.dataset.section || 'koi-favourite-card';
      for (let i = 0; i < missing.length; i += CHUNK) {
        await Promise.all(
          missing.slice(i, i + CHUNK).map(async (handle) => {
            try {
              const response = await fetch(
                `${root}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(section)}`
              );
              if (!response.ok) {
                console.warn('[koi-favourites] could not load', handle, response.status);
                return;
              }
              const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
              const card = doc.querySelector('[data-koi-fav-card]');
              // A deleted or unpublished product renders the section with no product: drop the handle.
              if (!card) {
                favourites.remove(handle);
                return;
              }
              this.#cards.set(handle, document.adoptNode(card));
            } catch (error) {
              console.warn('[koi-favourites]', handle, error);
            }
          })
        );
      }
      grid.removeAttribute('aria-busy');
      if (status) status.textContent = '';
    }

    for (const handle of handles) {
      const card = this.#cards.get(handle);
      if (card) grid.append(card);
    }
    const count = this.#cards.size;
    grid.hidden = count === 0;
    if (empty) empty.hidden = count > 0;

    this.#busy = false;
    if (this.#dirty) {
      this.#dirty = false;
      this.render();
    }
  }
}

for (const [name, ctor] of [
  ['koi-fav-toggle', KoiFavToggle],
  ['koi-fav-count', KoiFavCount],
  ['koi-fav-list', KoiFavList],
]) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}
